import React from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
    Box,
    ButtonBase,
    Collapse,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";

interface PrintoutSettingsAccordionProps {
    actionsLabel: string;
    children: React.ReactNode;
    disabled?: boolean;
    expanded: boolean;
    id: string;
    idPrefix: string;
    onOpenMenu: (anchorEl: HTMLElement) => void;
    onToggle: () => void;
    subtitle?: React.ReactNode;
    title: string;
}

const PrintoutSettingsAccordion = ({
    actionsLabel,
    children,
    disabled,
    expanded,
    id,
    idPrefix,
    onOpenMenu,
    onToggle,
    subtitle,
    title,
}: PrintoutSettingsAccordionProps) => {
    const detailsId = `${idPrefix}-${id}-details`;
    const headingId = `${idPrefix}-${id}-heading`;

    return (
        <Box
            sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            <Stack alignItems={'center'} direction={'row'} minHeight={subtitle ? 50 : 42} px={0.5}>
                <ButtonBase
                    aria-controls={detailsId}
                    aria-expanded={expanded}
                    disabled={disabled}
                    id={headingId}
                    onClick={onToggle}
                    sx={{
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        display: 'flex',
                        flex: 1,
                        gap: 0.5,
                        justifyContent: 'flex-start',
                        minWidth: 0,
                        px: 0.25,
                    }}
                >
                    <ChevronRightIcon
                        fontSize={'small'}
                        sx={{
                            color: 'text.secondary',
                            flex: '0 0 auto',
                            transform: expanded ? 'rotate(90deg)' : 'none',
                            transition: theme => theme.transitions.create('transform'),
                        }}
                    />
                    <Stack minWidth={0} textAlign={'left'}>
                        <Typography noWrap sx={{fontSize: 13, fontWeight: 600, lineHeight: 1.35}}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography color={'text.secondary'} noWrap sx={{fontSize: 11, lineHeight: 1.35}}>
                                {subtitle}
                            </Typography>
                        )}
                    </Stack>
                </ButtonBase>

                <IconButton
                    aria-label={actionsLabel}
                    disabled={disabled}
                    onClick={(event) => onOpenMenu(event.currentTarget)}
                    size={'small'}
                >
                    <MoreVertIcon fontSize={'small'}/>
                </IconButton>
            </Stack>

            <Collapse in={expanded} unmountOnExit>
                <Box
                    aria-labelledby={headingId}
                    id={detailsId}
                    role={'region'}
                    sx={{borderTop: 1, borderColor: 'divider', p: 1.5}}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};

export default PrintoutSettingsAccordion;
